/**
 * useFriends Hook
 * Fetches paginated friend list.
 * Shared between web and mobile.
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type { FriendInfo, PaginatedFriendList } from '../friendTypes';

// =============================================================================
// Types
// =============================================================================

export interface UseFriendsOptions {
  httpClient: HttpClient;
  isAuthenticated: boolean;
  pageSize?: number;
}

export interface UseFriendsResult {
  friends: FriendInfo[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  loadFriends: () => Promise<void>;
  loadMore: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useFriends(options: UseFriendsOptions): UseFriendsResult {
  const { httpClient, isAuthenticated, pageSize = 20 } = options;

  const [friends, setFriends] = useState<FriendInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFriends = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<PaginatedFriendList>('/api/friends', {
        limit: pageSize,
        offset: 0,
      });
      setFriends(data.friends);
      setTotal(data.total);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load friends');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, pageSize]);

  const loadMore = useCallback(async () => {
    if (!isAuthenticated || !hasMore || loading) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<PaginatedFriendList>('/api/friends', {
        limit: pageSize,
        offset: friends.length,
      });
      setFriends((prev) => [...prev, ...data.friends]);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more friends');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, hasMore, loading, friends.length, pageSize]);

  return {
    friends,
    total,
    hasMore,
    loading,
    error,
    loadFriends,
    loadMore,
  };
}
